import React, { useEffect, useState, useRef } from 'react';
import { useNavigationHandler } from '../utils/navigation';
import { useTagNavigation } from '../utils/tagNavigation';
import { fetchHomepageCaseStudies, fetchHomepageSnippets } from '../lib/sanity';
import '../components/ProjectList.css';
import '../pages/Snippets.css';

function MixedContentList() {
  const [mixedContent, setMixedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const videoRefs = useRef({});
  const { handleNavClick } = useNavigationHandler();
  const { navigateToWorkWithTag } = useTagNavigation();

  useEffect(() => {
    const loadMixedContent = async () => {
      try {
        const [caseStudies, snippets] = await Promise.all([
          fetchHomepageCaseStudies(),
          fetchHomepageSnippets()
        ]);

        // Transform case studies to include type and click handler
        const projectsWithType = caseStudies.map(project => ({
          ...project,
          type: 'project',
          id: project.slug.current,
          title: project.titleLineOne,
          secondTitle: project.titleLineTwo,
          mediaUrl: project.thumbnail,
          hoverMedia: project.hoverMedia,
          tags: project.tags,
          year: project.year,
          disabled: project.disabled
        }));

        // Transform snippets to include type
        const snippetsWithType = snippets.map(snippet => {
          let mediaUrl = null;
          let hoverMedia = null;

          // Get media URL based on media type
          switch (snippet.mediaType) {
            case 'single':
            default:
              if (snippet.media) {
                mediaUrl = snippet.media.type === 'video' 
                  ? snippet.media.video.asset.url 
                  : snippet.media.image.asset.url;
                if (snippet.media.type === 'video') {
                  hoverMedia = { type: 'video', url: mediaUrl };
                }
              }
              break;
            case 'dual':
              if (snippet.dualImages) {
                mediaUrl = snippet.dualImages.largeImage.asset.url;
              }
              break;
            case 'dualVideos':
              if (snippet.dualVideos) {
                mediaUrl = snippet.dualVideos.largeVideo.asset.url;
                hoverMedia = { type: 'video', url: mediaUrl };
              }
              break;
            case 'mixed':
              if (snippet.mixedMedia) {
                const largeMedia = snippet.mixedMedia.largeMedia;
                mediaUrl = largeMedia.type === 'video' 
                  ? largeMedia.video.asset.url 
                  : largeMedia.image.asset.url;
                if (largeMedia.type === 'video') {
                  hoverMedia = { type: 'video', url: mediaUrl };
                }
              }
              break;
          }

          return {
            ...snippet,
            type: 'snippet',
            id: snippet.name || `snippet-${snippet.date}`,
            title: snippet.name || 'Untitled Snippet',
            secondTitle: null,
            mediaUrl,
            hoverMedia,
            tags: [],
            year: snippet.year
          };
        });

        // Combine and sort by date (newest first)
        const combined = [...projectsWithType, ...snippetsWithType]
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        setMixedContent(combined);
      } catch (error) {
        console.error('Error loading mixed content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMixedContent();
  }, []);

  const handleItemClick = (item) => {
    if (item.type === 'project') {
      handleNavClick('case-study', item.id);
    }
    // Snippets don't have click navigation
  };

  const handleItemHover = (itemId, isHovering) => {
    const video = videoRefs.current[itemId];
    if (video) {
      if (isHovering) {
        video.play().catch(err => {
          console.error('Video autoplay prevented:', err);
        });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  };

  const renderSnippetMedia = (snippet) => {
    if (!snippet.mediaType) {
      return <div>No media type specified</div>;
    }

    switch (snippet.mediaType) {
      case 'single':
        if (!snippet.media) return <div>No media found</div>;
        
        return (
          <>
            {snippet.media.type === 'video' ? (
              <video
                src={snippet.media.video.asset.url}
                alt={snippet.media.alt}
                autoPlay
                loop
                muted
                playsInline
                className="snippet-image"
              />
            ) : (
              <img
                src={snippet.media.image.asset.url}
                alt={snippet.media.alt}
                className="snippet-image"
              />
            )}
          </>
        );

      case 'dual':
        if (!snippet.dualImages) return <div>No dual images found</div>;
        
        return (
          <div className="dual-images-container">
            <div className="large-image-container">
              <img
                src={snippet.dualImages.largeImage.asset.url}
                alt={snippet.dualImages.largeImageAlt}
                className="large-image"
              />
              <div className="title-container">
                <h3 className="snippet-title">{snippet.title}</h3>
                <span className="snippet-year">{snippet.year}</span>
              </div>
            </div>
            <div className="small-image-container">
              <img
                src={snippet.dualImages.smallImage.asset.url}
                alt={snippet.dualImages.smallImageAlt}
                className="small-image"
              />
            </div>
          </div>
        );

      case 'dualVideos':
        if (!snippet.dualVideos) return <div>No dual videos found</div>;
        
        return (
          <div className="dual-videos-container">
            <div className="large-video-container">
              <video
                src={snippet.dualVideos.largeVideo.asset.url}
                alt={snippet.dualVideos.largeVideoAlt}
                autoPlay
                loop
                muted
                playsInline
                className="large-video"
              />
              <div className="title-container">
                <h3 className="snippet-title">{snippet.title}</h3>
                <span className="snippet-year">{snippet.year}</span>
              </div>
            </div>
            <div className="small-video-container">
              <video
                src={snippet.dualVideos.smallVideo.asset.url}
                alt={snippet.dualVideos.smallVideoAlt}
                autoPlay
                loop
                muted
                playsInline
                className="small-video"
              />
            </div>
          </div>
        );

      case 'mixed':
        if (!snippet.mixedMedia) return <div>No mixed media found</div>;
        
        const renderMixedMedia = (mediaObj, className) => {
          if (mediaObj.type === 'video') {
            return (
              <video
                src={mediaObj.video.asset.url}
                alt={mediaObj.alt}
                autoPlay
                loop
                muted
                playsInline
                className={className}
              />
            );
          } else {
            return (
              <img
                src={mediaObj.image.asset.url}
                alt={mediaObj.alt}
                className={className}
              />
            );
          }
        };

        return (
          <div className="mixed-media-container">
            <div className="large-media-container">
              {renderMixedMedia(snippet.mixedMedia.largeMedia, 'large-media')}
              <div className="title-container">
                <h3 className="snippet-title">{snippet.title}</h3>
                <span className="snippet-year">{snippet.year}</span>
              </div>
            </div>
            <div className="small-media-container">
              {renderMixedMedia(snippet.mixedMedia.smallMedia, 'small-media')}
            </div>
          </div>
        );

      default:
        return <div>Unknown media type: {snippet.mediaType}</div>;
    }
  };

  if (loading) return <div className="project-list">Loading...</div>;
  if (mixedContent.length === 0) return null;

  return (
    <div className="project-list">
      <div className="project-list-container">
        {mixedContent.map((item, index) => {
          const isAlternate = index % 2 === 1;
          const hasSecondLine = item.secondTitle && item.secondTitle.trim() !== '';
          
          if (item.type === 'project') {
            // Render as project item
            const isDisabled = item.disabled;
            
            return (
              <div 
                key={item.id} 
                className={`project-item ${isAlternate ? 'project-item-reverse' : ''} ${isDisabled ? 'project-item-disabled' : ''}`}
                onClick={() => !isDisabled && handleItemClick(item)}
                onMouseEnter={() => !isDisabled && handleItemHover(item.id, true)}
                onMouseLeave={() => !isDisabled && handleItemHover(item.id, false)}
              >
                <div className="project-image-container">
                  <img 
                    src={item.mediaUrl} 
                    alt={item.title}
                    className={`project-image ${isDisabled ? 'project-image-disabled' : ''}`}
                  />
                  {item.hoverMedia && (
                    item.hoverMedia.type === 'video' ? (
                      <video
                        ref={el => videoRefs.current[item.id] = el}
                        src={item.hoverMedia.url}
                        className="project-hover-media"
                        muted
                        playsInline
                        loop
                      />
                    ) : item.hoverMedia.type === 'image' ? (
                      <img
                        src={item.hoverMedia.url}
                        alt={`${item.title} hover`}
                        className="project-hover-media"
                      />
                    ) : (
                      <img
                        src={item.mediaUrl}
                        alt={item.title}
                        className="project-hover-media"
                      />
                    )
                  )}
                </div>
                <div className="project-content">
                  <div className="title-container">
                    <span className="project-year">{item.year}</span>
                    <div className="title-line">
                      <h3 className="project-title project-title-top">
                        {item.title}
                      </h3>
                      <h3 className="project-title project-title-bottom">
                        {item.title}
                      </h3>
                    </div>
                    {hasSecondLine && (
                      <div className="title-line">
                        <h3 className="project-title project-title-top">
                          {item.secondTitle}
                        </h3>
                        <h3 className="project-title project-title-bottom">
                          {item.secondTitle}
                        </h3>
                      </div>
                    )}
                  </div>
                  {item.tags && item.tags.length > 0 && (
                    <div className="project-meta">
                      {item.tags.map((tag, index) => (
                        <span 
                          key={index} 
                          className="project-tag clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToWorkWithTag(tag);
                          }}
                        >
                          {tag.replace(/-/g, '\u2011')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          } else {
            // Render as snippet item
            return (
              <div 
                key={item.id} 
                className={`snippet-item ${isAlternate ? 'snippet-item-reverse' : ''}`}
                onMouseEnter={() => handleItemHover(item.id, true)}
                onMouseLeave={() => handleItemHover(item.id, false)}
              >
                <div className="snippet-image-container">
                  {renderSnippetMedia(item)}
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
}

export default MixedContentList; 